import styled from '@emotion/styled';
import { gettext } from '@eventbrite/i18n';
import React, { useCallback, useEffect } from 'react';

import {
    STATIC_MAP_HEIGHT,
    STATIC_MAP_MARKER_ICON,
    STATIC_MAP_SCALE,
    STATIC_MAP_STYLES,
    STATIC_MAP_ZOOM,
} from './constants';
import { getStaticMapURL } from './utils';

export interface StaticMapProps {
    latitude: string;
    longitude: string;
    location?: string;
    showMarker?: boolean;
}

export enum StaticMapIds {
    Container = 'StaticMapContainer',
}

type ImageSrcType = ReturnType<typeof getStaticMapURL>;
export const StaticMap = ({
    latitude,
    longitude,
    location = gettext('Enter a location').toString(),
    showMarker = true,
}: StaticMapProps) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [imageSrc, setImageSrc] = React.useState<ImageSrcType | null>(null);

    const center = `${latitude},${longitude}`;
    const height = STATIC_MAP_HEIGHT;

    const calculateImage = useCallback(() => {
        const width = containerRef.current?.clientWidth || 0;
        if (width > 0) {
            const markers = `scale:${STATIC_MAP_SCALE}|icon:${STATIC_MAP_MARKER_ICON}|${center}`;

            setImageSrc(
                getStaticMapURL({
                    center,
                    markers: showMarker ? markers : '',
                    zoom: STATIC_MAP_ZOOM,
                    width,
                    height,
                    scale: STATIC_MAP_SCALE,
                    styles: STATIC_MAP_STYLES,
                }),
            );
        }
    }, [center, height, showMarker]);

    useEffect(() => {
        calculateImage();
        // Need to copy the ref into another variable to overcome the issue
        // with react cleaning up the ref when unmounting the component.
        const containerElementCopy = containerRef.current;

        const observer = new ResizeObserver(calculateImage);
        if (containerElementCopy) {
            observer.observe(containerElementCopy);
        }

        return () => {
            observer.disconnect();
        };
    }, [containerRef, calculateImage]);

    return (
        <Container
            height={height}
            ref={containerRef}
            data-testid={StaticMapIds.Container}
        >
            {imageSrc && <Image src={imageSrc} alt={location} />}
        </Container>
    );
};

const Container = styled.div<{ height: number }>`
    border-radius: 8px;
    overflow: hidden;
    height: ${(props) => props.height}px;
`;

const Image = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;
